/**
 * dvShowBreathPoint.ts - 呼吸点动画效果扩展
 * 
 * 功能说明：
 * 1. 为折线图的最后一个数据点添加呼吸动画效果
 * 2. 通过阴影模糊度的变化实现呼吸效果
 * 3. 支持正常状态和强调状态的不同动画效果
 * 
 * 配置方式：
 * 在 series 配置中设置 dvShowBreathPoint: true 启用呼吸效果
 * 
 * 动画参数：
 * - 呼气时间：600ms，阴影模糊度：20px
 * - 吸气时间：900ms，阴影模糊度：10px
 * - 强调状态下阴影颜色变为红色
 */

import { EChartsExtensionInstallRegisters } from 'echarts/lib/extension';
import { Element } from 'zrender';
import { eachLineSeriesModel } from './helper';
import type { DvLineSeriesModel } from './type';

/**
 * 检查颜色是否可见（非透明）
 */
function isVisibleColor(color?: unknown) {
  return color && color !== 'transparent';
}

/**
 * 获取第一个可见的颜色
 */
function getFirstVisibleColor(colors: unknown[]) {
  for (let i = 0; i < colors.length; i++) {
    if (isVisibleColor(colors[i])) {
      return colors[i];
    }
  }
  return null;
}

/**
 * 获取最后一个非空数据点的索引
 */
function getLastIndexNotNull(points: ArrayLike<number>) {
  let len = points.length / 2;
  for (; len > 0; len--) {
    if (!(Number.isNaN(points[len * 2 - 2]) || Number.isNaN(points[len * 2 - 1]))) {
      break;
    }
  }

  return len - 1;
}

/**
 * 解析并应用呼吸点动画效果
 */
function parseBreathPoint(model: DvLineSeriesModel) {
  if (!model.get('dvShowBreathPoint')) {
    return;
  }

  const data = model.getData();
  const points = data.getLayout('points') as ArrayLike<number>;
  const lastIndex = getLastIndexNotNull(points);
  const lastSymbol = data.getItemGraphicEl(lastIndex);

  if (!lastSymbol) {
    return;
  }

  const symbolPath = lastSymbol.getSymbolPath() as Element;
  const symbolStyle = data.getItemVisual(lastIndex, 'style');
  const symbolShadowColor = getFirstVisibleColor([
    symbolStyle.shadowColor,
    symbolStyle.fill,
    symbolStyle.stroke
  ]);

  if (!symbolShadowColor) {
    return;
  }

  // 动画参数配置
  const exhaleTime = 600;
  const inhaleTime = 900;
  const exhaleShadowBlur = 20;
  const inhaleShadowBlur = 10;
  
  // 启动正常状态的呼吸动画
  symbolPath
    .animate('style', true)
    .when(exhaleTime, {
      shadowColor: symbolShadowColor,
      shadowBlur: exhaleShadowBlur
    })
    .when(inhaleTime, {
      shadowColor: symbolShadowColor,
      shadowBlur: inhaleShadowBlur
    })
    .start();

  // 状态转换完成后的回调处理（处理强调状态）
  symbolPath.stateTransition.done = () => {
    if (symbolPath.currentStates.includes('emphasis')) {
      symbolPath
        .animate('style', true)
        .when(exhaleTime, {
          shadowColor: '#red',
          shadowBlur: exhaleShadowBlur
        })
        .when(inhaleTime, {
          shadowColor: '#red',
          shadowBlur: inhaleShadowBlur
        })
        .start();
    }
  };
}

export function install(registers: EChartsExtensionInstallRegisters) {
  registers.registerUpdateLifecycle('series:afterupdate', (ecModel, api) => {
    eachLineSeriesModel(ecModel, model => {
      parseBreathPoint(model);
    });
  });
}
